/* eslint-disable no-underscore-dangle */
import React, { useState, useContext, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  IconButton,
  Box,
  Stack,
  Typography,
  FormControlLabel,
  Checkbox,
  MenuItem,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useMutation, useQuery } from '@apollo/client'

import { useMultiState } from '../../hooks'
import editUserMutation from '../../graphql/mutation/updateUserById'
import setPasswordMutation from '../../graphql/mutation/setPassword'
import userByIdQuery from '../../graphql/query/userById'
import AuthContext from '../../contexts/AuthContext'
import { useToast } from '../../contexts/ToastContext'

const initialState = {
  userName: '',
  loginName: '',
  pass1: '',
  pass2: '',
  role: 'Organizer',
  readOnly: true,
  disablePassword: false,
}

const EditUserDialog = ({ open, onClose, userId }) => {
  const { showSuccess, showError } = useToast()
  const [state, setState] = useMultiState(initialState)
  const [error, setError] = useState('')
  const { data } = useQuery(userByIdQuery, {
    variables: { id: userId },
    skip: !userId || !open,
  })
  const [editUser] = useMutation(editUserMutation)
  const [setPassword] = useMutation(setPasswordMutation)
  const { user } = useContext(AuthContext)

  useEffect(() => {
    if (data && data.userId) {
      setState({
        userName: data.userId.name || '',
        loginName: data.userId.username || '',
        role: data.userId.role || 'Organizer',
        readOnly: false,
      })
    }
  }, [data, setState])

  const roles = ['Organizer', 'Staff']
  if (user.role === 'Admin') {
    roles.push('Admin')
  }

  const handleClose = () => {
    setState(initialState)
    setError('')
    onClose()
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    if (!state.userName || !state.loginName) {
      setError('Please fill in all required fields')
      return
    }

    if (state.disablePassword && state.pass1 !== state.pass2) {
      setError('Passwords do not match')
      return
    }

    if (state.disablePassword && (!state.pass1 || !state.pass2)) {
      setError('Please fill in both password fields')
      return
    }

    try {
      const result = await editUser({
        variables: {
          record: {
            _id: userId,
            name: state.userName,
            username: state.loginName,
            role: state.role,
          },
        },
      })

      if (state.disablePassword) {
        await setPassword({
          variables: {
            id: result.data.updateUser.recordId,
            password: state.pass1,
          },
        })
      }

      showSuccess('User updated successfully!', 'Update Successful')
      handleClose()
    } catch (err) {
      console.error('Error updating user:', err)
      const errorMessage =
        err.message || err.graphQLErrors?.[0]?.message || 'Failed to update user. Please try again.'
      setError(errorMessage)
      showError(errorMessage, 'Update Failed')
    }
  }

  if (user.role === 'Staff') {
    return null
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: 1, sm: 1.25, md: 1.5 },
          backgroundColor: 'background.paper',
        },
      }}>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: { xs: 1.5, sm: 2, md: 2.5 },
          borderBottom: (theme) =>
            `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.12)'}`,
        }}>
        <Typography
          variant="h5"
          component="h2"
          sx={{
            fontWeight: 600,
            fontSize: { xs: '1.25rem', sm: '1.375rem', md: '1.5rem' },
            color: 'text.primary',
          }}>
          Edit User
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: 'text.secondary',
            '&:hover': {
              color: 'text.primary',
              backgroundColor: (theme) =>
                theme.palette.mode === 'light'
                  ? 'rgba(0, 0, 0, 0.04)'
                  : 'rgba(255, 255, 255, 0.08)',
            },
          }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit} autoComplete="off">
        <DialogContent sx={{ pt: { xs: 2, sm: 2.5, md: 3 } }}>
          <Stack spacing={{ xs: 2, sm: 2.5, md: 3 }}>
            {/* Row 1: Name and Username */}
            <Grid container spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Name"
                  value={state.userName}
                  onChange={(e) => setState({ userName: e.target.value })}
                  required
                  fullWidth
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                      borderRadius: { xs: 1, sm: 1.25, md: 1.5 },
                      backgroundColor: (theme) =>
                        theme.palette.mode === 'light'
                          ? 'rgba(0, 0, 0, 0.02)'
                          : 'rgba(255, 255, 255, 0.05)',
                      '&:hover': {
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'light'
                            ? 'rgba(0, 0, 0, 0.04)'
                            : 'rgba(255, 255, 255, 0.08)',
                      },
                      '&.Mui-focused': {
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'light'
                            ? 'rgba(0, 0, 0, 0.04)'
                            : 'rgba(255, 255, 255, 0.08)',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                      fontWeight: 500,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Username"
                  value={state.loginName}
                  onChange={(e) => setState({ loginName: e.target.value })}
                  required
                  fullWidth
                  size="small"
                  autoComplete="off"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                      borderRadius: { xs: 1, sm: 1.25, md: 1.5 },
                      backgroundColor: (theme) =>
                        theme.palette.mode === 'light'
                          ? 'rgba(0, 0, 0, 0.02)'
                          : 'rgba(255, 255, 255, 0.05)',
                      '&:hover': {
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'light'
                            ? 'rgba(0, 0, 0, 0.04)'
                            : 'rgba(255, 255, 255, 0.08)',
                      },
                      '&.Mui-focused': {
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'light'
                            ? 'rgba(0, 0, 0, 0.04)'
                            : 'rgba(255, 255, 255, 0.08)',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                      fontWeight: 500,
                    },
                  }}
                />
              </Grid>
            </Grid>

            {/* Edit Password Checkbox */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={state.disablePassword}
                  onChange={(e) => setState({ disablePassword: e.target.checked })}
                  sx={{
                    color: 'primary.main',
                    '&.Mui-checked': {
                      color: 'primary.main',
                    },
                  }}
                />
              }
              label="Edit Password"
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                  color: 'text.primary',
                },
              }}
            />

            {/* Row 2: Password and Confirm Password (only shown when Edit Password is checked) */}
            {state.disablePassword && (
              <Grid container spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Password"
                    placeholder="Password"
                    type="password"
                    value={state.pass1}
                    onChange={(e) => setState({ pass1: e.target.value })}
                    required={state.disablePassword}
                    fullWidth
                    size="small"
                    autoComplete="new-password"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: {
                          xs: '0.875rem',
                          sm: '0.9375rem',
                          md: '1rem',
                        },
                        borderRadius: { xs: 1, sm: 1.25, md: 1.5 },
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'light'
                            ? 'rgba(0, 0, 0, 0.02)'
                            : 'rgba(255, 255, 255, 0.05)',
                        '&:hover': {
                          backgroundColor: (theme) =>
                            theme.palette.mode === 'light'
                              ? 'rgba(0, 0, 0, 0.04)'
                              : 'rgba(255, 255, 255, 0.08)',
                        },
                        '&.Mui-focused': {
                          backgroundColor: (theme) =>
                            theme.palette.mode === 'light'
                              ? 'rgba(0, 0, 0, 0.04)'
                              : 'rgba(255, 255, 255, 0.08)',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: {
                          xs: '0.875rem',
                          sm: '0.9375rem',
                          md: '1rem',
                        },
                        fontWeight: 500,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Confirm Password"
                    placeholder="Confirm Password"
                    type="password"
                    value={state.pass2}
                    onChange={(e) => setState({ pass2: e.target.value })}
                    required={state.disablePassword}
                    fullWidth
                    size="small"
                    autoComplete="new-password"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: {
                          xs: '0.875rem',
                          sm: '0.9375rem',
                          md: '1rem',
                        },
                        borderRadius: { xs: 1, sm: 1.25, md: 1.5 },
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'light'
                            ? 'rgba(0, 0, 0, 0.02)'
                            : 'rgba(255, 255, 255, 0.05)',
                        '&:hover': {
                          backgroundColor: (theme) =>
                            theme.palette.mode === 'light'
                              ? 'rgba(0, 0, 0, 0.04)'
                              : 'rgba(255, 255, 255, 0.08)',
                        },
                        '&.Mui-focused': {
                          backgroundColor: (theme) =>
                            theme.palette.mode === 'light'
                              ? 'rgba(0, 0, 0, 0.04)'
                              : 'rgba(255, 255, 255, 0.08)',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: {
                          xs: '0.875rem',
                          sm: '0.9375rem',
                          md: '1rem',
                        },
                        fontWeight: 500,
                      },
                    }}
                  />
                </Grid>
              </Grid>
            )}

            {/* Row 3: Role */}
            <TextField
              select
              label="Role"
              value={state.role}
              onChange={(e) => setState({ role: e.target.value })}
              required
              fullWidth
              size="small"
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: {
                      borderRadius: { xs: 1, sm: 1.25, md: 1.5 },
                      mt: 0.5,
                    },
                  },
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                  borderRadius: { xs: 1, sm: 1.25, md: 1.5 },
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'light'
                      ? 'rgba(0, 0, 0, 0.02)'
                      : 'rgba(255, 255, 255, 0.05)',
                  '&:hover': {
                    backgroundColor: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'rgba(0, 0, 0, 0.04)'
                        : 'rgba(255, 255, 255, 0.08)',
                  },
                  '&.Mui-focused': {
                    backgroundColor: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'rgba(0, 0, 0, 0.04)'
                        : 'rgba(255, 255, 255, 0.08)',
                  },
                },
                '& .MuiInputLabel-root': {
                  fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                  fontWeight: 500,
                },
              }}>
              {roles.map((roleOption) => (
                <MenuItem key={roleOption} value={roleOption}>
                  {roleOption}
                </MenuItem>
              ))}
            </TextField>

            {error && (
              <Box
                sx={{
                  p: { xs: 1, sm: 1.25, md: 1.5 },
                  borderRadius: { xs: 0.75, sm: 1, md: 1.25 },
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'light'
                      ? 'rgba(244, 67, 54, 0.1)'
                      : 'rgba(244, 67, 54, 0.2)',
                  color: 'error.main',
                  fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                }}>
                {error}
              </Box>
            )}
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2, sm: 2.5, md: 3 },
            py: { xs: 1.5, sm: 2, md: 2.5 },
            borderTop: (theme) =>
              `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.12)'}`,
            gap: { xs: 1, sm: 1.5, md: 2 },
          }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            color="primary"
            sx={{
              borderRadius: { xs: 1, sm: 1.25, md: 1.5 },
              px: { xs: 2, sm: 2.5, md: 3 },
              py: { xs: 0.75, sm: 0.875, md: 1 },
              fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
              fontWeight: 500,
              textTransform: 'none',
            }}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            sx={{
              borderRadius: { xs: 1, sm: 1.25, md: 1.5 },
              px: { xs: 2, sm: 2.5, md: 3 },
              py: { xs: 0.75, sm: 0.875, md: 1 },
              fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
              fontWeight: 600,
              textTransform: 'none',
            }}>
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default EditUserDialog
